import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Login from "./pages/Login";
import PublicMenu from "./pages/PublicMenu";
import PublicReservation from "./pages/PublicReservation";
import Settings from "./pages/Settings";
import FoodCost from "./pages/FoodCost";
import Analytics from "./pages/Analytics";
import Register from "./pages/Register";
import Sales from "./pages/Sales";
import DashboardLayout from "./components/DashboardLayout";
import Dashboard from "./pages/Dashboard";
import MenuManager from "./pages/MenuManager";
import WhatsappMenuAdmin from "./pages/WhatsappMenuAdmin";
import Orders from "./pages/Orders";
import KdsBoard from "./pages/KdsBoard";
import { Wine, ChefHat, AlertTriangle } from "lucide-react";
import Reservations from "./pages/Reservations";
import Haccp from "./pages/Haccp";
import Accounting from "./pages/Accounting";
import Staff from "./pages/Staff";
import Shifts from "./pages/Shifts";
import ImportSales from "./pages/ImportSales";
import Pos from "./pages/Pos";
import DoganaAI from "./pages/DoganaAI";
import AiActions from "./pages/AiActions";
import FoodCostImport from "./pages/FoodCostImport";
import FoodCostIngredienti from "./pages/FoodCostIngredienti";
import FoodCostPending from "./pages/FoodCostPending";
import FoodCostRecipes from "./pages/FoodCostRecipes";
import Suppliers from "./pages/Suppliers";
import Customers from "./pages/Customers";
import Batches from "./pages/Batches";
import Inventario from "./pages/Inventario";
import Produzioni from "./pages/Produzioni";
import PublicSheet from "./pages/PublicSheet";
import PublicProduction from "./pages/PublicProduction";
import InspectionPortal from "./pages/InspectionPortal";
import QRCodes from "./pages/QRCodes";
import Privacy from "./pages/Privacy";
import DataDeletion from "./pages/DataDeletion";
import ClubDogana from "./pages/ClubDogana";
import ClubMe from "./pages/ClubMe";
import ClubVoucher from "./pages/ClubVoucher";
import CrmLoyalty from "./pages/CrmLoyalty";
import WaiterPreorder from "./pages/WaiterPreorder";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Caricamento…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const RoleRoute = ({ roles, children }) => {
  const { user } = useAuth();
  if (roles.includes(user?.role)) return children;
  return (
    <div className="flex flex-col items-start gap-3 max-w-lg" data-testid="module-forbidden">
      <p className="text-xs uppercase tracking-widest text-muted-foreground">Sezione non disponibile</p>
      <h1 className="font-dash text-3xl font-semibold">Non hai i permessi per questa area</h1>
    </div>
  );
};

const Mod = ({ module, admin, children }) => {
  const { user } = useAuth();
  const ok = user?.role === "admin" || (!admin && (!module || (user?.permissions || []).includes(module)));
  if (ok) return children;
  return (
    <div className="flex flex-col items-start gap-3 max-w-lg" data-testid="module-forbidden">
      <p className="text-xs uppercase tracking-widest text-muted-foreground">Sezione non disponibile</p>
      <h1 className="font-dash text-3xl font-semibold">Non hai i permessi per questa area</h1>
      <p className="text-sm text-muted-foreground">Chiedi al titolare di abilitare il modulo <b>{module || "richiesto"}</b> dal pannello Staff.</p>
      <Link to="/" className="text-primary underline text-sm" data-testid="module-forbidden-home">Torna alla panoramica</Link>
    </div>
  );
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/menu" element={<PublicMenu />} />
      <Route path="/prenota" element={<PublicReservation />} />
      <Route path="/scheda/:id" element={<PublicSheet />} />
      <Route path="/produzione/:token" element={<PublicProduction />} />
      <Route path="/ispezione/:token" element={<InspectionPortal />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/eliminazione-dati" element={<DataDeletion />} />
      <Route path="/club" element={<ClubDogana />} />
      <Route path="/club/me/:token" element={<ClubMe />} />
      <Route path="/club/voucher/:token" element={<ClubVoucher />} />
      <Route path="/login" element={<Login />} />
      <Route path="/cameriere" element={<Protected><RoleRoute roles={["admin", "manager", "cameriere"]}><WaiterPreorder /></RoleRoute></Protected>} />
      <Route
        path="/"
        element={
          <Protected>
            <DashboardLayout />
          </Protected>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="menu-gestione" element={<Mod module="menu"><MenuManager /></Mod>} />
        <Route path="whatsapp-menu-admin" element={<Mod module="menu"><WhatsappMenuAdmin /></Mod>} />
        <Route path="ordini" element={<Mod module="ordini"><Orders /></Mod>} />
        <Route path="pos" element={<Mod module="ordini"><Pos /></Mod>} />
        <Route path="qr-codes" element={<RoleRoute roles={["admin", "manager"]}><QRCodes /></RoleRoute>} />
        <Route path="loyalty" element={<Mod module="ordini"><CrmLoyalty /></Mod>} />
        <Route path="dogana-ai" element={<RoleRoute roles={["admin", "manager"]}><DoganaAI /></RoleRoute>} />
        <Route path="dogana-ai/azioni" element={<RoleRoute roles={["admin", "manager"]}><AiActions /></RoleRoute>} />
        <Route path="food-cost/import" element={<RoleRoute roles={["admin", "manager"]}><FoodCostImport /></RoleRoute>} />
        <Route path="food-cost/ingredienti" element={<RoleRoute roles={["admin", "manager"]}><FoodCostIngredienti /></RoleRoute>} />
        <Route path="food-cost/prodotti" element={<RoleRoute roles={["admin", "manager"]}><FoodCostPending /></RoleRoute>} />
        <Route path="food-cost/ricette" element={<RoleRoute roles={["admin", "manager"]}><FoodCostRecipes /></RoleRoute>} />
        <Route path="ordini/bar" element={<Mod module="ordini"><KdsBoard station="bar" title="Monitor Bar" Icon={Wine} /></Mod>} />
        <Route path="ordini/cucina" element={<Mod module="ordini"><KdsBoard station="cucina" title="Monitor Cucina" Icon={ChefHat} /></Mod>} />
        <Route path="ordini/non-assegnati" element={<Mod module="ordini"><KdsBoard station="unassigned" title="Non assegnati · rete di sicurezza" Icon={AlertTriangle} /></Mod>} />
        <Route path="prenotazioni" element={<Mod module="prenotazioni"><Reservations /></Mod>} />
        <Route path="magazzino" element={<Mod module="magazzino"><Batches /></Mod>} />
        <Route path="magazzino/inventari" element={<Mod module="magazzino"><Inventario /></Mod>} />
        <Route path="magazzino/produzioni" element={<Mod module="magazzino"><Produzioni /></Mod>} />
        <Route path="haccp" element={<Mod module="haccp"><Haccp /></Mod>} />
        <Route path="contabilita" element={<Mod module="contabilita"><Accounting /></Mod>} />
        <Route path="contabilita/registro/:tipo" element={<Mod module="contabilita"><Register /></Mod>} />
        <Route path="vendite" element={<Mod module="contabilita"><Sales /></Mod>} />
        <Route path="staff" element={<Mod module="staff"><Staff /></Mod>} />
        <Route path="turni" element={<Mod module="turni"><Shifts /></Mod>} />
        <Route path="import-cassa" element={<Mod module="import-cassa"><ImportSales /></Mod>} />
        <Route path="fornitori" element={<Mod module="fornitori"><Suppliers /></Mod>} />
        <Route path="lotti" element={<Navigate to="/magazzino" replace />} />
        <Route path="clienti" element={<Mod module="clienti"><Customers /></Mod>} />
        <Route path="food-cost" element={<Mod module="contabilita"><FoodCost /></Mod>} />
        <Route path="analisi" element={<Mod module="contabilita"><Analytics /></Mod>} />
        <Route path="impostazioni" element={<Mod admin><Settings /></Mod>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
